/** A folder of "All spaces" (one of the user's, or the built-in General), with its spaces in display order. */
export interface SidebarNavSpaceFolder {
  key: string;
  name: string;
  kind: "general" | "custom";
  sortMode: "custom" | "alpha" | "recent";
  collapsed: boolean;
  spaceKeys: string[];
}

/** What the user did; the app saves it and passes back updated `folders`. */
export interface SidebarFolderHandlers {
  onCreateFolder: (name: string) => void;
  /** "Move to new folder...": create it and put this space in it. */
  onCreateFolderWithSpace: (name: string, spaceKey: string) => void;
  onRenameFolder: (folderKey: string, name: string) => void;
  onDeleteFolder: (folderKey: string) => void;
  onSetFolderSort: (folderKey: string, sortMode: SidebarNavSpaceFolder["sortMode"]) => void;
  onToggleFolderCollapsed: (folderKey: string, collapsed: boolean) => void;
  /** The user's own folders in their new order (General always stays first). */
  onReorderFolders: (folderKeys: string[]) => void;
  /** `index` is the place in the folder's custom order; null = wherever its sort puts it (or the end). */
  onMoveSpace: (spaceKey: string, folderKey: string, index: number | null) => void;
  /** Takes a space out of whichever folder holds it, back to the loose/unfiled spaces. */
  onRemoveFromFolder: (spaceKey: string) => void;
  /** Opens one space's own settings. Omit it and "Space settings" is left out of the space menus. */
  onOpenSpaceSettings?: (spaceKey: string) => void;
}

/** Strings for folders. `{{name}}` is replaced with the space or folder name. */
export interface SidebarFolderLabels {
  newFolder: string;
  folderName: string;
  folderNamePlaceholder: string;
  /** The tooltip on a folder's "..." button. */
  folderMenu: string;
  /** The tooltip on a space's "..." button. */
  spaceMenu: string;
  /** What a screen reader says for those buttons; `{{name}}` is the folder or space. */
  folderMenuAria: string;
  spaceMenuAria: string;
  spaceSettings: string;
  rename: string;
  removeFolder: string;
  sortCustom: string;
  sortAlpha: string;
  sortRecent: string;
  moveTo: string;
  moveToNewFolder: string;
  removeFromFolder: string;
  moveUp: string;
  moveDown: string;
  /** Group label over Move up/down menu items. */
  moveGroup: string;
  /** Group label over "Move to {{name}}" / "Move to a new folder..." menu items. */
  moveToGroup: string;
  /** Group label over the three sort-order menu items. */
  sortGroup: string;
  /** Confirm shown when a space is dropped by hand into a sorted folder. */
  customOrderTitle: string;
  customOrderBody: string;
  customOrderConfirm: string;
  customOrderCancel: string;
  /** The button under the capped list of spaces not in a folder; `{{count}}` is how many there are, if you want it shown. */
  viewAll: string;
  /** Link at the bottom of the all-spaces panel, to the full page. */
  viewAllPage: string;
  searchSpaces: string;
  noResults: string;
  /** Shown under "All spaces" when the user has no spaces at all. */
  noSpaces: string;
  dragSpace: string;
  dragFolder: string;
  emptyFolder: string;
  dragInstructions: string;
  pickedUp: string;
  movedOver: string;
  dropped: string;
  cancelled: string;
}

export const DEFAULT_SIDEBAR_FOLDER_LABELS: SidebarFolderLabels = {
  newFolder: "New folder",
  folderName: "Folder name",
  folderNamePlaceholder: "Name this folder",
  folderMenu: "More actions",
  spaceMenu: "More actions",
  folderMenuAria: "More actions for {{name}}",
  spaceMenuAria: "More actions for {{name}}",
  spaceSettings: "Space settings",
  rename: "Rename",
  removeFolder: "Remove folder",
  sortCustom: "Sort by custom order",
  sortAlpha: "Sort A to Z",
  sortRecent: "Sort by recent activity",
  moveTo: "Move to {{name}}",
  moveToNewFolder: "Move to a new folder...",
  removeFromFolder: "Remove from folder",
  moveUp: "Move up",
  moveDown: "Move down",
  moveGroup: "Reorder",
  moveToGroup: "Move to",
  sortGroup: "Sort by",
  customOrderTitle: "Switch to custom order?",
  customOrderBody: "{{name}} is sorted automatically. To place spaces yourself, it needs to switch to custom order.",
  customOrderConfirm: "Switch to custom order",
  customOrderCancel: "Cancel",
  viewAll: "View all",
  viewAllPage: "View all spaces",
  searchSpaces: "Search spaces",
  noResults: "No spaces found",
  noSpaces: "No spaces yet",
  dragSpace: "Drag {{name}} to move",
  dragFolder: "Drag {{name}} to move",
  emptyFolder: "No spaces",
  dragInstructions:
    "To pick up an item, press Space or Enter. Use the arrow keys to move it, Space or Enter to drop it, Escape to cancel.",
  pickedUp: "Picked up {{name}}.",
  movedOver: "{{name}} is over {{target}}.",
  dropped: "Dropped {{name}}.",
  cancelled: "Cancelled. {{name}} is back where it was.",
};

export function fill(template: string, name: string, target = "", count = ""): string {
  return template.replaceAll("{{name}}", name).replaceAll("{{target}}", target).replaceAll("{{count}}", count);
}
